import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { edgeErrorMessage } from "@/lib/edge-errors";
import { invalidateFor } from "@/lib/cache-registry";
import type { SignalAgentInput } from "@/lib/signal-agent-calc";
import type { DBCustomerSignal } from "@/lib/db-types";

/** Et signal fra en prøvekjøring: ikke lagret, uten id og status. */
export type DryRunSignal = Pick<
  DBCustomerSignal,
  | "customer_id" | "signal_type" | "source" | "evidence_url" | "evidence_quote" | "headline"
  | "why_now" | "first_step" | "draft_subject" | "draft_body" | "strength"
> & { customer_name?: string | null };

export interface DryRunRun {
  customer_id: string | null;
  status: string;
  cost_nok: number;
  error?: string | null;
}

export interface DryRunResult {
  signals: DryRunSignal[];
  runs: DryRunRun[];
}

interface RunResponse extends Partial<DryRunResult> {
  skipped?: string;
  error?: string;
}

/**
 * «Prøvekjør på 3 kunder»: kjører en (ikke lagret) agentdefinisjon mot
 * utvalgte kunder uten å lagre noe (run-signal-agents med dry_run). Kostnaden
 * logges likevel i crm_agent_runs. Krever CRM_AGENTS_ENABLED på serveren.
 */
export function useDryRunSignalAgent() {
  return useMutation({
    mutationFn: async (vars: { agent: SignalAgentInput; customerIds: string[] }): Promise<DryRunResult> => {
      // Segmentagenter prøvekjøres mot de tre første nye kandidatene i segmentet, ikke mot kunder.
      const isSegment = vars.agent.scope_type === "segment";
      if (!isSegment && vars.customerIds.length === 0) throw new Error("Fant ingen overvåkede kunder å prøvekjøre mot");
      const { data, error } = await supabase.functions.invoke<RunResponse>("run-signal-agents", {
        body: { dry_run: true, ...(isSegment ? {} : { customer_ids: vars.customerIds.slice(0, 3) }), agent_draft: vars.agent },
      });
      if (error) throw new Error(await edgeErrorMessage(error, data, "Prøvekjøringen feilet"));
      if (data?.skipped === "flag_off") throw new Error("Signaler er ikke slått på ennå");
      if (data?.skipped === "agents_disabled") throw new Error("Agentkjøring er ikke slått på i dette miljøet (CRM_AGENTS_ENABLED)");
      if (data?.error) throw new Error(data.error);
      return { signals: data?.signals ?? [], runs: data?.runs ?? [] };
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export interface RunNowResult {
  customers: number;
  signals: number;
  deduped: number;
  failed: number;
  skipped: number;
  cost_nok: number;
}

/**
 * Admin: kjør alle aktive agenter mot én kunde nå (lagrer signaler og kostnad).
 * Samme kostnadsbryter og flagg som cron; kun admin slipper gjennom serveren.
 */
export function useRunSignalAgentsNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string): Promise<RunNowResult> => {
      const { data, error } = await supabase.functions.invoke<Partial<Omit<RunNowResult, "skipped">> & { skipped?: string | number; error?: string }>(
        "run-signal-agents",
        { body: { customer_ids: [customerId] } },
      );
      if (error) throw new Error(await edgeErrorMessage(error, data as { error?: string } | null, "Kjøringen feilet"));
      if (data?.skipped === "flag_off") throw new Error("Signaler er ikke slått på ennå");
      if (data?.skipped === "agents_disabled") throw new Error("Agentkjøring er ikke slått på i dette miljøet (CRM_AGENTS_ENABLED)");
      if (data?.error) throw new Error(data.error);
      return {
        customers: data?.customers ?? 0,
        signals: data?.signals ?? 0,
        deduped: data?.deduped ?? 0,
        failed: data?.failed ?? 0,
        skipped: typeof data?.skipped === "number" ? data.skipped : 0,
        cost_nok: data?.cost_nok ?? 0,
      };
    },
    onSuccess: async (result, customerId) => {
      await invalidateFor(qc, "customer_signals", { customerId });
      await invalidateFor(qc, "crm_agent_runs");
      toast.success(`Kjørte agentene: ${result.signals} nye signaler, ${result.failed} feilet`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
